import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/schedule-upload`;
}

/**
 * Appointment data structure returned from AI analysis
 */
export interface Appointment {
  time: string;
  date: string;
  patient_name: string;
  dob: string;
  report_name: string;
  location_code: string | null;
  confidence: number;
  notes: string | null;
}

/**
 * Response from schedule image analysis endpoint
 */
export interface AnalyzeScheduleResponse {
  schedule_date: string;
  appointments: Appointment[];
  total_appointments: number;
}

/**
 * Request body for creating appointments
 */
export interface CreateAppointmentsRequest {
  appointments: (Appointment & { clinician_id: string })[];
  timezone: string;
}

/**
 * Response from create appointments endpoint
 */
export interface CreateAppointmentsResponse {
  created: number;
  skipped: number;
  appointments: Array<{
    id: string;
    title: string;
    status: string;
    created_at: string;
  }>;
}

/**
 * Hook to analyze schedule image using AI
 */
export function useAnalyzeScheduleImage(options?: {
  onSuccess?: (data: AnalyzeScheduleResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await clientApi.post<AnalyzeScheduleResponse>(
        `${orgUrl(orgId!)}/analyze`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(
        `Found ${data.total_appointments} appointment${data.total_appointments !== 1 ? 's' : ''}`,
      );
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('Schedule analysis failed:', error);
      toast.error("Couldn't read the schedule. Try a clearer photo.");
      options?.onError?.(error as Error);
    },
  });
}

/**
 * Hook to create appointments from approved data
 */
export function useCreateAppointments(options?: {
  onSuccess?: (data: CreateAppointmentsResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useMutation({
    mutationFn: async (body: CreateAppointmentsRequest) => {
      const { data } = await clientApi.post<CreateAppointmentsResponse>(
        `${orgUrl(orgId!)}/create`,
        body,
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`${data.created} draft${data.created !== 1 ? 's' : ''} created`);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('Failed to create appointments:', error);
      toast.error('Something went wrong saving the drafts. Please try again.');
      options?.onError?.(error as Error);
    },
  });
}
